import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import DeleteModal from 'components/modals/DeleteModal';
import TabSelect from 'components/forms/basefields/TabSelect';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { hasAnyAllowedRight } from 'services/permissions/permissionChecks';
import NewsletterAudienceBuilder from './NewsletterAudienceBuilder';
import NewsletterAudiencePreview from './NewsletterAudiencePreview';
import { AudienceCondition, AudienceSource, NewsletterAudience, NEWSLETTER_AUDIENCES_PATH } from './NewsletterTypes';
import '../../../style/scss/forms.scss';
import '../../../style/scss/detailpage.scss';
import '../../../style/scss/newsletters.scss';

// The editable fields of an audience
interface AudienceFormState {
    name: string;
    source: AudienceSource;
    conditions: AudienceCondition[];
}

// The selectable sources of an audience, shown to the platform staff in the admin environment
const SOURCE_OPTIONS = [
    { value: 'contacts', name: 'newsletters.audiences.source_contacts' },
    { value: 'users', name: 'newsletters.audiences.source_users' },
];

// The page of an audience: the name, the description and the conditions with the live count
const NewsletterAudiencePage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const { initializeModal } = useModal();
    const { setFloatingAlert, setUnsavedChanges } = useGlobalContext();
    const { environmentType, allowedRights } = useAuthContext();
    const viewKey = useMemo(() => uuidv4(), []);
    const isNew = !id;

    // The platform users source is only offered to the platform staff inside the admin environment
    const canChooseSource = environmentType === 'admin' && hasAnyAllowedRight(allowedRights ?? [], ['no_user', 'staff_user']);

    // The id of the audience, none for a new audience
    const audienceId = id ? Number(id) : null;
    const [form, setForm] = useState<AudienceFormState>({ name: '', source: 'contacts', conditions: [] });
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const isDirtyRef = useRef(false);

    // Keep the latest context setters in refs since they change identity on every global render
    const setUnsavedChangesRef = useRef(setUnsavedChanges);
    const setFloatingAlertRef = useRef(setFloatingAlert);
    setUnsavedChangesRef.current = setUnsavedChanges;
    setFloatingAlertRef.current = setFloatingAlert;

    // Marks the form as dirty or clean and only touches the global state on a change
    const markDirty = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
        if (isDirtyRef.current === dirty) return;
        isDirtyRef.current = dirty;
        setUnsavedChangesRef.current(viewKey, dirty);
    }, [viewKey]);

    // Fetch the existing audience
    const { response: audience } = useFetchData({ apiUrl: isNew ? '' : `get_newsletter_audience/${id}` });

    // Fill the form with the fetched audience
    useEffect(() => {
        if (!audience) return;
        const fetched = audience as NewsletterAudience;
        setForm({ name: fetched.name ?? '', source: fetched.source ?? 'contacts', conditions: fetched.conditions ?? [] });
    }, [audience]);

    // Clear the unsaved changes state when leaving the page
    useEffect(() => () => setUnsavedChangesRef.current(viewKey, false), [viewKey]);

    // Updates a field of the form and marks the form as dirty
    const updateField = <K extends keyof AudienceFormState>(field: K, value: AudienceFormState[K]) => {
        setForm(current => ({ ...current, [field]: value }));
        markDirty(true);
    };

    // Changes the source, the conditions of the other source do not apply anymore
    const changeSource = (source: string) => {
        if (source !== 'contacts' && source !== 'users') return;
        setForm(current => (current.source === source ? current : { ...current, source, conditions: [] }));
        markDirty(true);
    };

    // Saves the audience and returns to the overview
    const saveAudience = async () => {
        if (isSaving) return;
        if (!form.name.trim()) {
            setErrors({ name: 'newsletters.audiences.name_required' });
            return;
        }
        setIsSaving(true);
        setErrors({});

        try {
            // Post a new audience or patch the existing one
            const data = { name: form.name.trim(), source: form.source, conditions: form.conditions };
            if (audienceId) {
                await saveData({ apiUrl: `patch_newsletter_audience/${audienceId}`, method: 'patch', data });
            } else {
                await saveData({ apiUrl: 'post_newsletter_audience', method: 'post', data });
            }

            // Show the confirmation and return to the overview
            markDirty(false);
            setFloatingAlertRef.current({ type: 'success', message: 'newsletters.audiences.saved_message' });
            history.push(NEWSLETTER_AUDIENCES_PATH);
        } catch (error: any) {
            // Show the field errors or a general alert
            const responseErrors = error?.response?.data;
            if (responseErrors && typeof responseErrors === 'object') {
                const fieldErrors: Record<string, string> = {};
                Object.entries(responseErrors).forEach(([field, message]) => { fieldErrors[field] = Array.isArray(message) ? String(message[0]) : String(message); });
                setErrors(fieldErrors);
            }
            setFloatingAlertRef.current({ type: 'danger', message: 'newsletters.general.save_failed_message' });
        } finally {
            setIsSaving(false);
        }
    };

    // Opens the delete confirmation of the audience
    const handleDelete = () => {
        if (!audienceId) { history.push(NEWSLETTER_AUDIENCES_PATH); return; }
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `patch_newsletter_audience/${audienceId}`, method: 'patch', data: { deleted: true } });
                    markDirty(false);
                    history.push(NEWSLETTER_AUDIENCES_PATH);
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'newsletters.general.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    return (
        <div className='detailpage newsletter-audience-page container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button' onClick={() => history.push(NEWSLETTER_AUDIENCES_PATH)}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>
                        {isNew ? t('newsletters.audiences.page_title_new') : (form.name || t('newsletters.audiences.page_title'))}
                    </h2>
                </div>
                <div className='header-buttons'>
                    {isDirty && <span className='newsletter-unsaved-indicator'>{t('newsletters.editor.unsaved')}</span>}
                    {!isNew && (
                        <SecondaryButton label='general.delete' size='small' icon={<FontAwesomeIcon icon={faTrash} />} onClick={handleDelete} />
                    )}
                </div>
            </div>

            <div className='detailpage-content'>
                <div>
                    {/* The details and the conditions of the audience */}
                    <div className='card'>
                        <h6 className='newsletter-card-header'>{t('newsletters.audiences.details_title')}</h6>
                        <div className='field-group'>
                            <div className='edit-mode'>
                                <label>{t('newsletters.audiences.name_label')}</label>
                                <input
                                    type='text'
                                    id='name'
                                    name='name'
                                    placeholder={t('newsletters.audiences.name_placeholder')}
                                    value={form.name}
                                    onChange={(event) => updateField('name', event.target.value)}
                                    className={`text ${errors.name ? 'is-invalid' : ''}`} />
                                {errors.name && <div className='error-message'>{t(errors.name)}</div>}
                            </div>
                            {canChooseSource && (
                                <div className='edit-mode'>
                                    <label>{t('newsletters.audiences.source_title')}</label>
                                    <TabSelect<any>
                                        options={SOURCE_OPTIONS}
                                        selectedOption={SOURCE_OPTIONS.find(option => option.value === form.source) ?? null}
                                        optionFormat='name'
                                        onChange={changeSource} />
                                    <p className='helper-text'>{t('newsletters.audiences.source_intro')}</p>
                                </div>
                            )}
                        </div>

                        <NewsletterAudienceBuilder
                            conditions={form.conditions}
                            source={form.source}
                            onChange={(conditions) => updateField('conditions', conditions)}
                            errorMessage={errors.conditions}
                            title='newsletters.audiences.conditions_title'
                            intro={form.source === 'users' ? 'newsletters.audiences.conditions_intro_users' : 'newsletters.audiences.conditions_intro'} />
                    </div>
                </div>

                <div>
                    {/* The recipients which match the conditions right now */}
                    <div className='card'>
                        <NewsletterAudiencePreview conditions={form.conditions} source={form.source} />
                    </div>
                </div>
            </div>

            <div className='footer-fixed'>
                <div className='container-filled'>
                    <SecondaryButton
                        onClick={() => history.push(NEWSLETTER_AUDIENCES_PATH)}
                        label='general.cancel'
                        size='small' />
                    <PrimaryButton
                        onClick={saveAudience}
                        label='general.save'
                        size='small'
                        loading={isSaving} />
                </div>
            </div>
        </div>
    );
};

export default NewsletterAudiencePage;
